import {
  useState,
  useContext,
  createContext,
  useEffect,
  ReactNode,
} from 'react';
import { useRouter } from 'next/router';
import {
  Snackbar,
  Alert,
  Grow,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  ThemeProvider,
  AlertColor,
  Breakpoint,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { v4 as uuidv4 } from 'uuid';
import { datadogRum } from '@datadog/browser-rum';
import { defaultTheme, GridItem } from './ui-extension';

interface NotificationState {
  open: boolean;
  message?: string;
  title?: string;
  severity?: AlertColor;
  useDialog?: boolean;
  handleConfirm?: () => void;
  confirmText?: string;
  handleCancel?: () => void;
  cancelText?: string;
  onClose?: () => void;
  content?: ReactNode;
  hideActions?: boolean;
  fullWidth?: boolean;
  maxWidth?: Breakpoint | false;
  autoHideDuration?: number | null;
  // Opt-in backdrop close. Boolean for a fixed choice, or a guard function evaluated
  // at click time so the content can decide based on its current state (e.g. unsaved input).
  closeOnBackdrop?: boolean | (() => boolean);
  // When a backdrop click is blocked, show this themed warning instead of doing nothing,
  // e.g. to tell the user their unsaved data would be lost.
  backdropWarningMessage?: string;
}

interface DialogOptions {
  handleConfirm?: () => void;
  confirmText?: string;
  handleCancel?: () => void;
  cancelText?: string;
  title?: string;
  message?: string;
  onClose?: () => void;
  content?: ReactNode;
  hideActions?: boolean;
  fullWidth?: boolean;
  maxWidth?: Breakpoint | false;
  closeOnBackdrop?: boolean | (() => boolean);
  backdropWarningMessage?: string;
}

interface ShowMessageOptions {
  message: string;
  title?: string;
  severity?: AlertColor;
  duration?: number;
}

interface AxiosLikeError {
  response?: {
    data?:
      | {
          type?: string;
          arrayBuffer?: () => Promise<ArrayBuffer>;
          text?: () => Promise<string>;
          message?: string;
        }
      | string;
  };
  toString: () => string;
}

type ExceptionHandlerAction<T, P extends unknown[]> = (
  ...params: P
) => Promise<T>;
type ExceptionHandlerFinalAction = () => Promise<void> | void;
type ExceptionHandlerOnError = (error: unknown) => Promise<void> | void;

interface NotificationContextType {
  useExceptionHandler: <T, P extends unknown[]>(
    action: ExceptionHandlerAction<T, P>,
    finalAction?: ExceptionHandlerFinalAction,
    onError?: ExceptionHandlerOnError
  ) => (...params: P) => Promise<T | undefined>;
  handleException: (e: AxiosLikeError, title?: string) => void;
  openDialog: (options: DialogOptions) => void;
  showMessage: (options: ShowMessageOptions) => void;
  closePopup: () => void;
  cache: Record<string, unknown>;
  updateCache: (object: Record<string, unknown>) => void;
  queryParameters: Record<string, string | string[] | undefined>;
  updateQueryParameters: (
    queries?: Record<string, string | number | null | undefined>
  ) => void;
  windowHeight: number;
}

export const NotificationContext = createContext<NotificationContextType>(
  {} as NotificationContextType
);

// TODO: This context should be renamed as "AppContext".
export function useNotificationContext(): NotificationContextType {
  return useContext(NotificationContext);
}

export const defaultWindowHeight = 960;

interface NotificationProps {
  children: ReactNode;
}

export const Notification = ({ children }: NotificationProps) => {
  const router = useRouter();
  const { query } = router;

  const [notification, setNotificationState] = useState<NotificationState>({
    open: false,
  });
  const [cache, setCache] = useState<Record<string, unknown>>({});
  const [queryParameters, setQueryParameters] = useState<
    Record<string, string | undefined>
  >({});
  const updateCache = (object: Record<string, unknown>) => {
    setCache((prev) => ({ ...prev, ...object }));
  };
  const [windowHeight, setWindowHeight] = useState(defaultWindowHeight);
  // Shown when a backdrop click is blocked, so the user understands why the dialog stayed open.
  const [showBackdropWarning, setShowBackdropWarning] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    setWindowHeight(window.innerHeight);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    for (const key in Object.keys(query)) {
      if (Array.isArray(key)) {
        query[key] = (query[key] as string[]).join(',');
      }
    }
    setQueryParameters({ ...(query as Record<string, string>) });
  }, [query]);

  const updateQueryParameters = (
    queries: Record<string, string | number | null | undefined> = {}
  ) => {
    // Query strings are always passed in as lower case from P21 ribbon metrics
    if (!queries) return;

    let isQueryParameterUpdated = false;

    for (const key in queries) {
      if (query[key] === queries[key]) {
        continue;
      }

      isQueryParameterUpdated = true;

      if (!queries[key] && queries[key] !== 0) {
        delete query[key];
      } else {
        query[key] = String(queries[key]);
      }
    }

    if (isQueryParameterUpdated) {
      router.push(router);
    }
  };

  const showErrorNotification = (e: AxiosLikeError, title?: string) => {
    console.error(e);

    const errorData = e.response?.data;
    let message: string;

    // Handle Axios error response;
    if (errorData) {
      message = `${
        typeof errorData === 'string'
          ? errorData
          : (errorData as { message?: string }).message ||
            JSON.stringify(errorData)
      }`;
    } else {
      message = e.toString();
    }

    setNotificationState((prev) => ({
      ...prev,
      open: true,
      message: message,
      title: title,
      severity: 'error',
    }));

    datadogRum.addError(e);
  };

  const handleException = (e: AxiosLikeError, title?: string) => {
    const responseData = e.response?.data;
    if (
      typeof responseData === 'object' &&
      responseData?.type === 'application/json' &&
      responseData?.arrayBuffer &&
      responseData?.text
    ) {
      responseData.text().then((text: string) => {
        e.response = e.response || {};
        e.response.data = JSON.parse(text);

        showErrorNotification(e, title);
      });
      return;
    }

    showErrorNotification(e, title);
  };

  const openDialog = ({
    handleConfirm,
    confirmText = 'Yes',
    handleCancel,
    cancelText = 'No',
    title,
    message,
    onClose,
    content,
    hideActions,
    fullWidth,
    maxWidth = 'xl',
    closeOnBackdrop,
    backdropWarningMessage,
  }: DialogOptions) => {
    setNotificationState((prev) => ({
      ...prev,
      open: true,
      useDialog: true,
      message: message,
      title: title,
      handleConfirm: handleConfirm,
      confirmText: confirmText,
      handleCancel: handleCancel,
      cancelText: cancelText,
      onClose: onClose,
      content,
      hideActions,
      fullWidth,
      maxWidth,
      closeOnBackdrop,
      backdropWarningMessage,
    }));
  };

  const closePopup = () => {
    setNotificationState({ open: false });
  };

  const handleConfirm = () => {
    if (notification.handleConfirm) {
      notification.handleConfirm();
    }
    closePopup();
  };

  const handleCancel = () => {
    if (notification.handleCancel) {
      notification.handleCancel();
    }
    closePopup();
  };

  const onClose = () => {
    if (notification.onClose) {
      notification.onClose();
    }
    closePopup();
  };

  const showMessage = ({
    message,
    title,
    severity = 'success',
    duration = null, // eventually hide...
  }: ShowMessageOptions) => {
    setNotificationState((prev) => ({
      ...prev,
      open: true,
      message: message,
      title: title,
      severity: severity,
      autoHideDuration: duration,
    }));
  };

  const useExceptionHandler = <T, P extends unknown[]>(
    action: ExceptionHandlerAction<T, P>,
    finalAction?: ExceptionHandlerFinalAction,
    onError?: ExceptionHandlerOnError
  ) => {
    return async (...params: P): Promise<T | undefined> => {
      let response: T | undefined;

      try {
        response = await action(...params);
      } catch (error) {
        onError
          ? await onError(error)
          : handleException(error as AxiosLikeError);
      }
      await finalAction?.();

      return response;
    };
  };

  const sharedContext: NotificationContextType = {
    useExceptionHandler,
    handleException,
    openDialog,
    showMessage,
    closePopup,
    cache,
    updateCache,
    queryParameters,
    updateQueryParameters,
    windowHeight,
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <NotificationContext.Provider value={sharedContext}>
        {children}
        {notification.useDialog ? (
          <Dialog
            open={notification.open}
            onClose={(_event, reason) => {
              // Backdrop clicks only close when the dialog opts in. The guard can be a
              // function so content (e.g. a form with unsaved input) decides at click time.
              if (reason === 'backdropClick') {
                const allowBackdropClose =
                  typeof notification.closeOnBackdrop === 'function'
                    ? notification.closeOnBackdrop()
                    : notification.closeOnBackdrop;
                if (!allowBackdropClose) {
                  // Warn the user (if configured) instead of silently ignoring the click.
                  if (notification.backdropWarningMessage)
                    setShowBackdropWarning(true);
                  return;
                }
              }
              onClose();
            }}
            TransitionComponent={Grow}
            maxWidth={notification.maxWidth}
            fullWidth={!!notification.fullWidth}
          >
            {notification.title && (
              <DialogTitle sx={{ paddingRight: '48px' }}>
                {notification.title}
                {/* Give the user an explicit way out, since backdrop clicks are ignored and some dialogs hide the action buttons. */}
                <IconButton
                  aria-label='close'
                  onClick={onClose}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
            )}
            {notification.message && (
              <DialogContent>
                <DialogContentText>{notification.message}</DialogContentText>
              </DialogContent>
            )}
            {notification.content && (
              <DialogContent>{notification.content}</DialogContent>
            )}
            {!notification.hideActions && (
              //  Match the padding of Dialog Title and Dialog Content for consistency.
              <DialogActions sx={{ padding: '0 24px 20px 24px' }}>
                <Grid container justifyContent='space-between'>
                  <GridItem>
                    <Button
                      variant='outlined'
                      autoFocus
                      onClick={handleConfirm}
                    >
                      {notification.confirmText}
                    </Button>
                  </GridItem>
                  <GridItem>
                    <Button
                      variant='outlined'
                      onClick={handleCancel}
                      color='error'
                    >
                      {notification.cancelText}
                    </Button>
                  </GridItem>
                </Grid>
              </DialogActions>
            )}
          </Dialog>
        ) : (
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={notification.open}
            onClose={onClose}
            message={notification.message}
            key={uuidv4()}
            autoHideDuration={notification.autoHideDuration || null}
            TransitionComponent={Grow}
            // Render above dialogs (incl. ones that raise their own zIndex to 1500)
            // so backdrop-close warnings aren't hidden behind the dialog backdrop.
            sx={{ zIndex: (theme) => theme.zIndex.modal + 300 }}
          >
            {notification.severity ? (
              <Alert
                variant='standard'
                elevation={6}
                onClose={onClose}
                severity={notification.severity}
                sx={{ minWidth: '25vw' }}
              >
                {notification.message}
              </Alert>
            ) : undefined}
          </Snackbar>
        )}
        {/* Separate snackbar so a backdrop-close warning can appear over an open dialog. */}
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={showBackdropWarning}
          onClose={() => setShowBackdropWarning(false)}
          autoHideDuration={5000}
          TransitionComponent={Grow}
        >
          <Alert
            variant='standard'
            elevation={6}
            severity='warning'
            onClose={() => setShowBackdropWarning(false)}
            sx={{ minWidth: '25vw' }}
          >
            {notification.backdropWarningMessage}
          </Alert>
        </Snackbar>
      </NotificationContext.Provider>
    </ThemeProvider>
  );
};
